import { IconName, Tag } from '@clarion-blueprint-shim';
import { ReactNode } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { ClarionButtonLink } from '../../clarion/ClarionButtonLink';
import { cx } from '../../util';
import { useSideNavigation } from './SideNavigationContext';

type NavigationItemProps = {
  to: string;
  icon?: IconName | React.JSX.Element;
  children: ReactNode;
  onClick?: () => void;
  badge?: string | number; // For notifications/counts
};

export function NavigationItem({ to, icon, children, onClick, badge }: NavigationItemProps) {
  const location = useLocation();
  const { location: pendingLocation } = useNavigation();
  const { isCollapsed } = useSideNavigation();

  const isActiveRoute = location.pathname === to || location.pathname.startsWith(to + '/');
  const isPendingRoute =
    pendingLocation?.pathname === to || pendingLocation?.pathname.startsWith(to + '/');

  return (
    <ClarionButtonLink
      href={to}
      onClick={onClick}
      variant={isActiveRoute ? 'selected' : 'minimal'}
      icon={icon}
      intent={isActiveRoute ? 'primary' : 'none'}
      fill
      size={isCollapsed ? 'large' : 'medium'}
      textAlign="left"
      tooltip={isCollapsed ? children?.toString() : undefined}
      tooltipPlacement="right"
      className={cx(
        'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-150 justify-start',
        'hover:bg-gray-100 dark:hover:bg-gray-700',
        isPendingRoute && 'animation-pulse cursor-wait',
      )}
    >
      {!isCollapsed && (
        <>
          <span className="truncate flex-1 grow">{children}</span>
          {!!badge && !isCollapsed && (
            <Tag minimal intent="primary" round className="float-end">
              {badge}
            </Tag>
          )}
        </>
      )}
    </ClarionButtonLink>
  );
}
